import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Modal, Typography } from '@mui/material';
import { Formik, Form } from 'formik';
import LoadingButton from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import { format, parse } from 'date-fns';
import { validationSchema } from './validations/create-project-member-validation-schema';
import { useMutation, useQuery } from '@apollo/client';
import Allocation from '../allocation';
import { errorToast, successToast } from '../../../common/utils/alert';
import { errorMessages } from '../../../common/utils/constants';
import {
  CREATE_PROJECT_MEMBERS,
  GET_PROJECT,
  GET_PROJECT_ROLES,
  GET_SEARCH_MEMBERS
} from '../../gql';
import DatePickerComponent from '../../../common/widgets/date-picker';
import CheckBoxComponent from '../../../common/widgets/check-box';
import MultipleAutoCompleteComponent from '../../../common/widgets/multiple-auto-complete';
import AutoCompleteSearchComponent from '../../../common/widgets/auto-complete-search';
import { useAppSelector } from '../../../../redux/hooks';
import { AllocationType } from '../project-member-allocation-grid';
import { UserRoleTypes } from '../../type';
import {
  isOperationsLead,
  isOperationsLeadAndSuperAdmin
} from '../../../common/utils/helperFunctions';

export interface formValues {
  projectMemberName: string;
  projectMemberRole: string;
  fromDate: string;
  toDate: string;
  projectMemberAllocation: string;
  isMaximumAllocation: boolean;
}

interface ICreateProjectMemberProps {
  closeRequestForm: () => void;
  openRequestForm: () => void;
  projectId: string;
  updateProjectMemberCallback: () => Promise<void>;
  isVisible: boolean;
}

const CreateProjectMemberModal = ({
  closeRequestForm,
  openRequestForm,
  projectId,
  updateProjectMemberCallback,
  isVisible
}: ICreateProjectMemberProps): JSX.Element => {
  const [createLoading, setCreateLoading] = useState(false);
  const [searchingMemberId, setSearchingMemberId] = useState<string>();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [tentativeStartDate, setTentativeStartDate] = useState<Date>();
  const [tentativeEndDate, setTentativeEndDate] = useState<Date>();
  const [actionUserId, setActionUserId] = useState<string>('');
  const [userRoles, setUserRoles] = useState<UserRoleTypes>({
    isOperationsLead: false,
    isSuperAdmin: false
  });
  const { profileData } = useAppSelector((state) => state.profileData);
  const [projectMemberAllocationValue, setProjectMemberAllocationValue] =
    useState<string>();
  const [startDateChecked, setStartDateChecked] = useState(false);
  const [endDateChecked, setEndDateChecked] = useState(false);
  const [suggestedAllocationlist, setSuggestedAllocationList] = useState<
    AllocationType[] | null
  >(null);

  const { data: projectData } = useQuery(GET_PROJECT, {
    variables: { id: projectId }
  });

  const [createProjectMember] = useMutation(CREATE_PROJECT_MEMBERS);

  const initialFormState: formValues = {
    projectMemberName: '',
    projectMemberRole: '',
    fromDate: '',
    toDate: '',
    projectMemberAllocation: '',
    isMaximumAllocation: false
  };

  useEffect(() => {
    if (projectData) {
      setTentativeStartDate(
        new Date(projectData.getProject.tentativeStartDate)
      );
      setTentativeEndDate(new Date(projectData.getProject.tentativeEndDate));
    }
  }, [projectData]);

  useEffect(() => {
    if (profileData) {
      if (profileData.memberId) {
        setActionUserId(profileData.memberId);
      }
      if (isOperationsLeadAndSuperAdmin(profileData?.roles)) {
        setUserRoles({
          isOperationsLead: true,
          isSuperAdmin: true
        });
      } else if (isOperationsLead(profileData?.roles)) {
        setUserRoles({
          isOperationsLead: true,
          isSuperAdmin: false
        });
      }
    }
  }, [profileData]);

  const handleRequest = async (
    values: formValues,
    { resetForm }: { resetForm: Function }
  ): Promise<void> => {
    const selectedRoles = Array.isArray(values.projectMemberRole)
      ? values.projectMemberRole.map((role) => role.id)
      : [];

    closeRequestForm();
    setCreateLoading(true);

    await createProjectMember({
      variables: {
        actionUserId,
        suggestedAllocations: JSON.stringify(suggestedAllocationlist),
        projectId,
        memberId: values.projectMemberName,
        projectRolesIds: selectedRoles,
        roleStartDate: format(
          new Date(values.fromDate),
          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
        ),
        roleEndDate: format(
          new Date(values.toDate),
          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
        ),
        hoursPerWeek: parseInt(projectMemberAllocationValue ?? '')
      }
    })
      .then(async () => {
        if (
          (userRoles.isOperationsLead || userRoles.isSuperAdmin) &&
          suggestedAllocationlist?.length === 0
        ) {
          successToast('Project Member Added Successfully');
        } else {
          successToast('Request sent for approval');
        }
        await updateProjectMemberCallback();
        resetForm({ values: '' });
        setSearchingMemberId('');
        setProjectMemberAllocationValue('');
        setFromDate(null);
        setToDate(null);
        setStartDateChecked(false);
        setEndDateChecked(false);
      })
      .catch(() => {
        errorToast(errorMessages.ErrorMessageCreateUserInProject);
      })
      .finally(() => {
        setCreateLoading(false);
      });
  };

  const handleCancel = (): void => {
    closeRequestForm();
    setSearchingMemberId('');
    setProjectMemberAllocationValue('');
    setFromDate(null);
    setToDate(null);
    setStartDateChecked(false);
    setEndDateChecked(false);
  };

  const projectMembers: Array<Record<string, string | number>> = [];

  for (let i = 0; i < projectData?.getProject?.members.length; i++) {
    projectMembers.push(projectData?.getProject?.members[i].member.id);
  }

  return (
    <Modal
      open={isVisible}
      onClose={closeRequestForm}
      sx={{
        marginTop: '25px',
        overflow: 'scroll'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '24px',
          p: '35px'
        }}
      >
        <Formik
          initialValues={initialFormState}
          onSubmit={handleRequest}
          validationSchema={validationSchema}
        >
          {({ setFieldValue, errors, touched, values }) => (
            <Form data-testid="form-test">
              <Box
                sx={{
                  maxWidth: '678px',
                  padding: '5px 5px 5px 5px',
                  borderRadius: '28px',
                  boxShadow: 'none',
                  background:
                    'linear-gradient(0deg, rgba(41, 93, 171, 0.11), rgba(41, 93, 171, 0.11)), #FDFBFF'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center'
                  }}
                >
                  <IconButton
                    sx={{ paddingTop: '10px' }}
                    onClick={handleCancel}
                  >
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    padding: '0px 20px'
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '24px',
                        fontWeight: 500,
                        color: '#44474E',
                        marginBottom: '30px'
                      }}
                    >
                      Add Member
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '15px'
                    }}
                  >
                    <Box>
                      <AutoCompleteSearchComponent
                        label={'Name'}
                        query={{
                          query: GET_SEARCH_MEMBERS,
                          getter: 'getMembers'
                        }}
                        optionLabelKey={'displayName'}
                        onChange={(newValue) => {
                          setSuggestedAllocationList([]);
                          if (newValue) {
                            setSearchingMemberId(newValue.id.toString());
                            void setFieldValue(
                              'projectMemberName',
                              newValue.id
                            );
                          } else {
                            setSearchingMemberId('');
                          }
                        }}
                        touched={touched.projectMemberName}
                        errors={errors.projectMemberName}
                        projectMembers={projectMembers}
                      />
                    </Box>

                    <Box>
                      <MultipleAutoCompleteComponent
                        label={'Role'}
                        query={{
                          query: GET_PROJECT_ROLES,
                          getter: 'getProjectRoles'
                        }}
                        optionLabelKey={'displayName'}
                        onChange={function (
                          newValue: Array<Record<string, string | number>>
                        ): void {
                          if (newValue) {
                            void setFieldValue('projectMemberRole', newValue);
                          }
                        }}
                        touched={touched.projectMemberRole}
                        errors={errors.projectMemberRole}
                      />
                    </Box>

                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          flexWrap: 'wrap',
                          gap: '15px'
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <DatePickerComponent
                            label={'Resource Start Date'}
                            value={fromDate}
                            format={'MM/dd/yyyy'}
                            disablePast={true}
                            onChange={(event: Date | null) => {
                              if (event) {
                                const formattedDate = format(
                                  event,
                                  'MM/dd/yyyy'
                                );
                                void setFieldValue('fromDate', formattedDate);
                                setFromDate(event);
                                if (
                                  event.getTime() ===
                                  tentativeStartDate?.getTime()
                                ) {
                                  setStartDateChecked(true);
                                } else {
                                  setStartDateChecked(false);
                                }
                              }
                            }}
                            touched={touched.fromDate}
                            errors={errors.fromDate}
                          />

                          <CheckBoxComponent
                            label={'Use the project’s start date'}
                            isChecked={startDateChecked}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const isChecked = event.target.checked;
                              setStartDateChecked(isChecked);
                              if (isChecked && tentativeStartDate) {
                                const formattedStartDate = format(
                                  tentativeStartDate,
                                  'MM/dd/yyyy'
                                );
                                void setFieldValue(
                                  'fromDate',
                                  formattedStartDate
                                );
                                setFromDate(tentativeStartDate);
                              }
                            }}
                          />
                        </Box>

                        <Box sx={{ flexGrow: 1 }}>
                          <DatePickerComponent
                            label={'Resource End Date'}
                            minDate={parse(
                              values.fromDate,
                              'MM/dd/yyyy',
                              new Date()
                            )}
                            value={toDate}
                            format={'MM/dd/yyyy'}
                            onChange={function (event: Date | null): void {
                              if (event) {
                                const formattedDate = format(
                                  event,
                                  'MM/dd/yyyy'
                                );
                                void setFieldValue('toDate', formattedDate);
                                setToDate(event);
                                if (
                                  event.getTime() ===
                                  tentativeEndDate?.getTime()
                                ) {
                                  setEndDateChecked(true);
                                } else {
                                  setEndDateChecked(false);
                                }
                              }
                            }}
                            touched={touched.toDate}
                            errors={errors.toDate}
                          />
                          <CheckBoxComponent
                            label={'Use the project’s end date'}
                            isChecked={endDateChecked}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              const isChecked = event.target.checked;
                              setEndDateChecked(isChecked);
                              if (isChecked && tentativeEndDate) {
                                const formattedEndDate = format(
                                  tentativeEndDate,
                                  'MM/dd/yyyy'
                                );
                                void setFieldValue('toDate', formattedEndDate);
                                setToDate(tentativeEndDate);
                              }
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    {searchingMemberId && projectId && (
                      <Allocation
                        projectId={projectId}
                        memberId={searchingMemberId}
                        isAdd={true}
                        value={projectMemberAllocationValue}
                        onChange={(value) => {
                          setProjectMemberAllocationValue(value);
                          void setFieldValue('projectMemberAllocation', value);
                        }}
                        touched={
                          touched.projectMemberAllocation ??
                          touched.isMaximumAllocation
                        }
                        errors={
                          errors.projectMemberAllocation ??
                          errors.isMaximumAllocation
                        }
                        setSuggestedAllocationList={setSuggestedAllocationList}
                        setIsMaximumAllocation={(value) => {
                          void setFieldValue('isMaximumAllocation', value);
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: '15px',
                    marginRight: '10px',
                    marginBottom: '10px'
                  }}
                >
                  <Button
                    onClick={handleCancel}
                    disabled={createLoading}
                    sx={{
                      padding: '10px 12px 10px 12px',
                      fontSize: 18,
                      fontWeight: 500,
                      color: '#295DAB',
                      textTransform: 'none'
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    disabled={createLoading}
                    loading={createLoading}
                    sx={{
                      padding: '10px 12px 10px 12px',
                      fontSize: '18px',
                      fontWeight: 500,
                      color: '#295DAB',
                      textTransform: 'none'
                    }}
                  >
                    Save
                  </LoadingButton>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};

export default CreateProjectMemberModal;
